import { notification } from 'antd';
import { t } from 'i18next';
import { formatNumberHelper } from '../helpers';
import UploadProgressService from './UploadProgessService';

const initialState = {
  uploading: false,
  uploaded: false,
  uploadProgress: 0,
  uploadProcessed: 0,
  uploadTotal: 0,
  uploadMinimized: false,
  restoreRequested: false,
  showResultsRequested: false,
  activeUploadType: null,
  processName: null,
  resultsUpload: [],
  errorsUpload: null,
  bajaFiles: null,
};

class UploadProgressManager {
  state = { ...initialState };

  progressListener = null;

  activeUploadBatchId = null;

  subscribers = new Set();

  onCompleteHook = null;

  subscribe(listener) {
    this.subscribers.add(listener);
    listener(this.state);
    return () => this.subscribers.delete(listener);
  }

  notify() {
    this.subscribers.forEach((listener) => listener(this.state));
  }

  setState(partial) {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  getState() {
    return this.state;
  }

  isInProgress() {
    return this.state.uploading && !this.state.uploaded;
  }

  setOnCompleteHook(hook) {
    this.onCompleteHook = hook;
  }

  getUploadTypeTitle(uploadType = this.state.activeUploadType) {
    if (uploadType === 'inactive') {
      return t('messages.aml.entityUploadInactivate');
    }
    if (uploadType === 'related') {
      return t('messages.aml.related');
    }
    return t('messages.aml.entityUpload');
  }

  start(uploadType) {
    this.setState({
      uploading: true,
      uploaded: false,
      uploadProgress: 0,
      uploadProcessed: 0,
      uploadTotal: 0,
      uploadMinimized: false,
      restoreRequested: false,
      showResultsRequested: false,
      activeUploadType: uploadType,
      processName: null,
      resultsUpload: [],
      errorsUpload: null,
      bajaFiles: null,
    });
  }

  minimize() {
    if (!this.isInProgress()) {
      return;
    }
    this.setState({
      uploadMinimized: true,
      restoreRequested: false,
    });
  }

  ensureMinimizedOnLeave() {
    if (this.isInProgress() && !this.state.uploadMinimized) {
      this.minimize();
    }
  }

  requestRestore() {
    if (!this.isInProgress() && !this.state.uploaded) {
      return false;
    }

    this.setState({
      restoreRequested: true,
      uploadMinimized: false,
    });

    return true;
  }

  clearRestoreRequest() {
    if (this.state.restoreRequested) {
      this.setState({ restoreRequested: false });
    }
  }

  clearShowResultsRequest() {
    if (this.state.showResultsRequested) {
      this.setState({ showResultsRequested: false });
    }
  }

  closeListener() {
    if (this.progressListener) {
      try {
        this.progressListener.close();
      } catch (e) {
        /* ignore */
      }
      this.progressListener = null;
    }
  }

  reset() {
    this.closeListener();
    this.activeUploadBatchId = null;
    this.state = { ...initialState };
    this.notify();
  }

  buildResultsUpload(result) {
    const resultsUpload = [];

    Object.keys(result || {}).forEach((key) => {
      let line = '';
      if (key === 'OK') line = `${t('messages.aml.loadedEntities')}: `;
      else if (key === 'UPD') line = `${t('messages.aml.recordsModified')}: `;
      else if (key === 'INAC') line = `${t('messages.aml.inactiveEntities')}: `;
      else if (key === 'E01' || key === 'E02') line = `${t('messages.aml.entitiesExists')}: `;
      else if (key === 'RC') line = `${t('messages.aml.relation.added')}: `;
      else if (key === 'E03') line = `${t('messages.aml.entitiesDontExists')}: `;
      else if (key === 'E_VAL') line = `${t('messages.aml.error.row.validation')}: `;
      else if (key === 'E05') line = `${t('messages.aml.duplicated.row')}: `;
      else if (key === 'DEL_OK') line = `${t('messages.aml.deleted.releated')}: `;
      else line = `${t('messages.aml.recordsWithErrors')}: `;

      line += formatNumberHelper(result[key]);
      resultsUpload.push(line);
    });

    return resultsUpload;
  }

  openResultsModal(uploadType) {
    this.setState({
      showResultsRequested: true,
      uploadMinimized: false,
      activeUploadType: uploadType,
    });
  }

  listen(batchId, uploadType) {
    this.closeListener();
    this.activeUploadBatchId = batchId;

    this.setState({
      uploading: true,
      uploaded: false,
      activeUploadType: uploadType,
      restoreRequested: false,
      showResultsRequested: false,
    });

    this.progressListener = UploadProgressService.listenProgress(batchId, {
      onProgress: (progressData) => {
        if (batchId !== this.activeUploadBatchId) {
          return;
        }

        this.setState({
          uploadProgress: progressData.progress,
          processName: progressData.processName,
          uploadProcessed: progressData.processed || 0,
          uploadTotal: progressData.total || progressData.lines || 0,
          activeUploadType: uploadType,
          uploading: true,
          uploaded: false,
        });
      },
      onComplete: (progressData) => {
        if (batchId !== this.activeUploadBatchId) {
          return;
        }

        this.closeListener();
        this.activeUploadBatchId = null;

        if (progressData.status === 'ERROR') {
          notification.error({
            message: t('messages.aml.error'),
            description: progressData.message || t('messages.aml.errorProcessingFile'),
          });
          this.setState({
            uploading: false,
            uploadMinimized: false,
            restoreRequested: false,
          });
          this.openResultsModal(uploadType);
          return;
        }

        const { result, errors, summaryFile } = progressData;
        const resultsUpload = this.buildResultsUpload(result);

        this.setState({
          resultsUpload,
          uploading: false,
          uploaded: true,
          uploadMinimized: false,
          restoreRequested: false,
          errorsUpload: errors,
          bajaFiles: summaryFile || null,
          activeUploadType: uploadType,
        });

        if (this.onCompleteHook) {
          this.onCompleteHook();
        }

        notification.success({
          message: t('messages.aml.successfulOperation'),
          description: t('messages.aml.processFinishedSuccessfully'),
        });

        this.openResultsModal(uploadType);
      },
      onInactivity: () => {
        notification.warning({
          message: t('messages.aml.warning'),
          description: t('messages.aml.uploadInactivity'),
        });
      },
      onError: (error) => {
        if (batchId !== this.activeUploadBatchId) {
          return;
        }

        this.closeListener();
        this.activeUploadBatchId = null;

        notification.error({
          message: t('messages.aml.error'),
          description: error && error.message ? error.message : t('messages.aml.errorProcessingFile'),
        });

        this.setState({
          uploading: false,
          uploadMinimized: false,
          restoreRequested: false,
        });
        this.openResultsModal(uploadType);
      },
    });
  }
}

export default new UploadProgressManager();
